import { useCallback, useEffect, useMemo, useRef, type ChangeEvent, type ReactElement } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { Box, TextField, useTheme } from '@mui/material'
import { replaceTrailingFormulaPlainText } from '../MarkdownEditor/extensions/MathCalculator'

function stripStoredMarkup(value: string): string {
  if (!value.includes('<')) return value
  if (typeof document !== 'undefined') {
    const el = document.createElement('div')
    el.innerHTML = value
    return el.textContent ?? ''
  }
  return value.replace(/<[^>]+>/g, '')
}

type InnerProps = {
  value: string | undefined
  onChange: (next: string) => void
  onBlur?: () => void
  /** RHF field.ref callback — wires focus registration to the textarea. */
  assignInputRef?: (el: HTMLTextAreaElement | null) => void
  placeholder?: string
  disabled?: boolean
  minRows?: number
  formulaResultDecimalPlaces: number
}

const FormulaPlainTextFieldInner = ({
  value,
  onChange,
  onBlur,
  assignInputRef,
  placeholder = '',
  disabled = false,
  minRows = 3,
  formulaResultDecimalPlaces,
}: InnerProps): ReactElement => {
  const theme = useTheme()
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    const raw = value ?? ''
    if (!raw.includes('<')) return
    const stripped = stripStoredMarkup(raw)
    if (stripped !== raw) {
      onChange(stripped)
    }
  }, [value, onChange])

  const displayValue = useMemo(() => stripStoredMarkup(value ?? ''), [value])

  const mergeInputRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      textareaRef.current = el
      assignInputRef?.(el)
    },
    [assignInputRef],
  )

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      const caret = e.target.selectionStart ?? raw.length
      const { text: replaced, caretIndex } = replaceTrailingFormulaPlainText(
        raw,
        formulaResultDecimalPlaces,
        caret,
      )
      onChange(replaced)
      if (replaced !== raw && caretIndex !== null) {
        queueMicrotask(() => {
          const el = textareaRef.current
          if (!el) return
          el.setSelectionRange(caretIndex, caretIndex)
        })
      }
    },
    [formulaResultDecimalPlaces, onChange],
  )

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        value={displayValue}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        multiline
        minRows={minRows}
        fullWidth
        inputRef={mergeInputRef}
        variant="outlined"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: 13,
            fontFamily: 'Roboto',
            lineHeight: 1.45,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : theme.palette.background.paper,
          },
          '& .MuiOutlinedInput-inputMultiline': {
            maxHeight: 204,
            overflow: 'auto',
            padding: '8px 12px',
          },
        }}
      />
    </Box>
  )
}

export type FormulaPlainTextFieldProps =
  | {
      name: string
      placeholder?: string
      disabled?: boolean
      minRows?: number
      formulaResultDecimalPlaces?: number
    }
  | {
      value: string
      onChange: (value: string) => void
      onBlur?: () => void
      placeholder?: string
      disabled?: boolean
      minRows?: number
      formulaResultDecimalPlaces?: number
    }

const FormulaPlainTextFieldWithName = ({
  name,
  placeholder,
  disabled,
  minRows,
  formulaResultDecimalPlaces = 2,
}: {
  name: string
  placeholder?: string
  disabled?: boolean
  minRows?: number
  formulaResultDecimalPlaces?: number
}): ReactElement => {
  const { control } = useFormContext()
  const {
    field: { onChange, onBlur, value, ref },
  } = useController({ name, control })

  return (
    <FormulaPlainTextFieldInner
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      assignInputRef={ref}
      placeholder={placeholder}
      disabled={disabled}
      minRows={minRows}
      formulaResultDecimalPlaces={formulaResultDecimalPlaces}
    />
  )
}

const FormulaPlainTextFieldControlled = ({
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  minRows,
  formulaResultDecimalPlaces = 2,
}: {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  minRows?: number
  formulaResultDecimalPlaces?: number
}): ReactElement => (
  <FormulaPlainTextFieldInner
    value={value}
    onChange={onChange}
    onBlur={onBlur}
    placeholder={placeholder}
    disabled={disabled}
    minRows={minRows}
    formulaResultDecimalPlaces={formulaResultDecimalPlaces}
  />
)

const FormulaPlainTextField = (props: FormulaPlainTextFieldProps): ReactElement => {
  if ('name' in props) {
    return <FormulaPlainTextFieldWithName {...props} />
  }
  return <FormulaPlainTextFieldControlled {...props} />
}

export default FormulaPlainTextField
